/**
 * Protofac geo allow/block list — the single source of truth shared by the signup-gate UI
 * (country dropdown) and the backend server-side validator. Frontend and backend MUST agree,
 * so the classification lives here in `@voice-assistant/shared`.
 *
 * Framework: `accounting/docs/legal/cross-border-vat-overview.md` (Stripe-direct + B2B-only).
 *   Tier 1 — all sales open (no foreign-seller registration at year-1 scale).
 *   Tier 2 — B2B only; requires a VIES-validated VAT ID at signup (B2C deferred: OSS/HMRC/Н-18).
 *   Tier 3 — blocked at signup (registration burden too heavy, or sanctioned) → waitlist capture.
 *
 * Country codes are ISO-3166-1 alpha-2, uppercase.
 */

export type CountryTier = 1 | 2 | 3;

/** Tier 1 — fully open (B2B + B2C). */
export const TIER1_COUNTRIES: readonly string[] = ['US', 'CA', 'AU', 'NZ', 'JP', 'SG', 'IL'];

/** EU-27 (all reverse-charge B2B lanes). */
export const EU27_COUNTRIES: readonly string[] = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
];

/** Tier 2 — B2B only (EU-27 + UK). Requires a valid VAT ID (VIES-validated) at signup. */
export const TIER2_COUNTRIES: readonly string[] = [...EU27_COUNTRIES, 'GB'];

/**
 * Explicitly sanctioned / embargoed — always Tier 3, never unblockable by demand.
 * (Distinct from "Tier 3 by registration burden", which may be promoted as MRR grows.)
 */
export const SANCTIONED_COUNTRIES: readonly string[] = ['RU', 'BY', 'KP', 'IR', 'SY', 'CU'];

const TIER1_SET = new Set(TIER1_COUNTRIES);
const TIER2_SET = new Set(TIER2_COUNTRIES);
const SANCTIONED_SET = new Set(SANCTIONED_COUNTRIES);

/** Normalize to uppercase ISO alpha-2; returns '' for falsy input. */
export function normalizeCountry(code: string | null | undefined): string {
  return (code ?? '').trim().toUpperCase();
}

/**
 * Classify a country into its Protofac tier. Anything not in Tier 1 or Tier 2 — including
 * unknown/empty codes — is Tier 3 (fail-closed: we never accidentally open an unlisted market).
 */
export function getCountryTier(code: string | null | undefined): CountryTier {
  const c = normalizeCountry(code);
  if (TIER1_SET.has(c)) return 1;
  if (TIER2_SET.has(c)) return 2;
  return 3;
}

export function isSanctionedCountry(code: string | null | undefined): boolean {
  return SANCTIONED_SET.has(normalizeCountry(code));
}

/** Tier 3 → blocked at signup (waitlist). */
export function isBlockedCountry(code: string | null | undefined): boolean {
  return getCountryTier(code) === 3;
}

/** Tier 2 → allowed only as B2B; the signup gate must collect a VIES-validated VAT ID. */
export function requiresVatId(code: string | null | undefined): boolean {
  return getCountryTier(code) === 2;
}

export interface GeoDecision {
  country: string;
  tier: CountryTier;
  allowed: boolean;          // false → blocked at signup (Tier 3)
  requiresVatId: boolean;    // true → Tier 2, B2B VAT ID required
  sanctioned: boolean;
  reason: string;
}

/** Pure decision used by both the FE preview and the BE enforcement (BE is authoritative). */
export function evaluateCountry(code: string | null | undefined): GeoDecision {
  const country = normalizeCountry(code);
  const tier = getCountryTier(country);
  const sanctioned = isSanctionedCountry(country);
  return {
    country,
    tier,
    allowed: tier !== 3,
    requiresVatId: tier === 2,
    sanctioned,
    reason:
      tier === 1 ? 'open'
      : tier === 2 ? 'b2b-only-vat-required'
      : sanctioned ? 'sanctioned'
      : 'not-yet-available',
  };
}

/** Result of validating a Tier-2 VAT identifier (VIES for EU-27; format-only for GB until HMRC). */
export interface VatValidationResult {
  valid: boolean;
  method: 'vies' | 'gb-format' | 'none';
  vatId: string;
  countryCode: string;
  name?: string;
  reason?: string;
}

/** Split a raw VAT id ("DE123456789" / "de 123 456 789") into ISO country prefix + number. */
export function parseVatId(raw: string | null | undefined): { countryCode: string; number: string } {
  const v = (raw ?? '').toUpperCase().replace(/[\s-]/g, '');
  const m = v.match(/^([A-Z]{2})(.+)$/);
  return m ? { countryCode: m[1], number: m[2] } : { countryCode: '', number: v };
}
